import { cn, Stack, Tooltip, TooltipContent, TooltipTrigger } from 'component-library';
import { useTranslation } from 'react-i18next';
import { TooltipProps } from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import { IndicatorName } from '@/api/rest/resources/types/fact';
import { WidgetType } from '@/api/rest/resources/types/widget';
import { ChartTooltip, ChartTooltipTextPrimary } from '@/components/Charts/components/Tooltip';
import { ChartShape } from '@/components/widgets/o/CompositeGraph/CompositeGraph';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { isNullish } from '@/utils/booleans';

import { Icon } from '../a/Icon/Icon';

const chartColors = {
  color: '#5EB8C8',
  fill: '#A8D8E8',
} as const;

const FACT_TYPES = [
  IndicatorName.structural_integrity_score,
  IndicatorName.functional_integrity_score,
  IndicatorName.compositional_integrity_score,
] as const;
type FACT_TYPE = (typeof FACT_TYPES)[number];

const EIIDecompositionTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
  const { t } = useTranslation();

  const data = payload?.[0];
  const factType = (data?.payload as EIIDecompositionChartElement)?.name ?? '';

  const value = data?.value as number | undefined;

  const eiiDisplay = !isNullish(value)
    ? `${getDisplayNumber(value, window.navigator.language)}`
    : t('global.analysis.noData');

  if (!active || !payload?.length) return null;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextPrimary>
          {t(`global.widget.ecologicalIntegrityIndex.decomposition.${factType as FACT_TYPE}.label`)}
        </ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{eiiDisplay}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

const EIIAxisTick = (props: {
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  payload?: { value: string };
  data?: EIIDecompositionChartElement[];
}) => {
  const { t } = useTranslation();
  const { x = 0, y = 0, cx = 0, cy = 0, payload, data = [] } = props;

  const value = payload?.value ?? '';
  const fact = data.find((d) => d.name === value);

  if (!fact) return null;

  const dx = x - cx;
  const dy = y - cy;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const labelX = x + (dx / len) * 14 - 56;
  const labelY = y + (dy / len) * 14 - 28;

  return (
    <foreignObject x={labelX} y={labelY} width={112} height={72} style={{ overflow: 'visible' }}>
      <div className='typography-caption w-fit whitespace-pre-wrap rounded-lg font-semibold text-text-primary p-1 bg-neutral-black-4 flex flex-row gap-1 items-center'>
        <Tooltip>
          <TooltipTrigger className='items-center'>
            <Icon icon='ri-information-line' className='ml-1 text-active-54 typography-body1' />
          </TooltipTrigger>
          <TooltipContent className='whitespace-pre-wrap'>
            <Stack>
              {t(`global.widget.ecologicalIntegrityIndex.decomposition.${fact.name as FACT_TYPE}.tooltip.body`)}
            </Stack>
          </TooltipContent>
        </Tooltip>
        <span className='px-1'>
          {t(`global.widget.ecologicalIntegrityIndex.decomposition.${fact.name as FACT_TYPE}.label`)}
        </span>
      </div>
    </foreignObject>
  );
};

type EIIDecompositionChartElement = {
  name: string;
  value: number;
};

type EIIDecompositionChartProps = {
  styles?: Record<string, string>;
};

export const useEIIDecompositionChart = (props?: EIIDecompositionChartProps) => {
  const { t } = useTranslation();

  const facts = useOrganizationIndicatorReportingFact<number>([
    IndicatorName.ecological_integrity_score,
    ...FACT_TYPES,
  ]);

  const ecologicalIntegrityIndex = facts[IndicatorName.ecological_integrity_score]?.value;
  const formattedTotalEIIValue = ecologicalIntegrityIndex ? getDisplayNumber(ecologicalIntegrityIndex) : '--';

  const chartData: EIIDecompositionChartElement[] = FACT_TYPES.flatMap((factType) => {
    const factValue = facts[`${factType}`]?.value;
    if (isNullish(factValue) || typeof factValue !== 'number') return [];
    return [{ name: factType, value: factValue }];
  });

  const noChartData = chartData.length === 0;

  return {
    name: 'eii_decomposition_card',
    type: WidgetType.o_card,
    visible: !noChartData,
    relatedData: {
      header: t('shared.ncaDetail.details.labels.eii'),
      headerTooltip: t('global.widget.ecologicalIntegrityIndexTooltip'),
      styles: {
        root: cn('h-full rounded-2xl w-full', props?.styles?.root ?? ''),
        header: cn(
          'typography-overline pt-4 pl-4 justify-center items-center text-text-secondary border-none',
          props?.styles?.header ?? '',
        ),
        body: cn('h-full py-0 flex flex-col justify-center gap-4 border-none', props?.styles?.body ?? ''),
      },
    },
    widgets: [
      {
        name: 'ecological_integrity_value',
        type: WidgetType.a_text,
        visible: true,
        relatedData: {
          text: formattedTotalEIIValue,
          className: 'pl-2 typography-h2 text-text-primary',
        },
      },
      {
        name: 'eii_decomposition_graph',
        type: WidgetType.o_composite_graph,
        visible: true,
        relatedData: {
          'data-testid': 'eii-decomposition-radar-chart',
          data: chartData,
          chartConfig: [
            {
              shape: ChartShape.radar,
              dataKey: 'value',
              label: t('shared.ncaDetail.details.ecosystemIntegrityIndex.explainers.historicalEii.title'),
              color: chartColors.color,
              fill: chartColors.fill,
              fillOpacity: 0.5,
              dot: false,
              key: 'eii_score',
              id: 'eii_score',
            },
          ],
          xAxisProps: {
            dataKey: 'name',
            tick: (tickProps: Parameters<typeof EIIAxisTick>[0]) => <EIIAxisTick {...tickProps} data={chartData} />,
            outerRadius: '60%',
          },
          yAxisProps: {
            domain: [0, 1],
            tickCount: 3,
            tick: false,
            axisLine: false,
          },
          tooltip: EIIDecompositionTooltip,
        },
      },
    ],
  };
};
